<script setup>
import { computed } from 'vue';
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  showMessageType: {
    type: Boolean,
    default: true,
  },
  defaultEmptyMessage: {
    type: String,
    default: '',
  },
  multiLine: {
    type: Boolean,
    default: false,
  },
});

const { getPlainText } = useMessageFormatter();

const attachmentIcons = {
  image: 'i-lucide-image',
  audio: 'i-lucide-headphones',
  video: 'i-lucide-video',
  file: 'i-lucide-file',
  location: 'i-lucide-map-pin',
  fallback: 'i-lucide-link-2',
};

const messageByAgent = computed(() => {
  const type = props.message?.message_type ?? props.message?.messageType;
  return type === MESSAGE_TYPE.OUTGOING || type === 'outgoing' || type === 1;
});

const statusIcon = computed(() => {
  const status = (props.message?.status || '').toString().toLowerCase();
  if (status === 'progress') return 'i-lucide-clock';
  if (status === 'read' || status === 'delivered' || status === '1' || status === '2') return 'i-lucide-check-check';
  return 'i-lucide-check';
});

const statusColor = computed(() => {
  const status = (props.message?.status || '').toString().toLowerCase();
  if (status === 'read' || status === '2') return 'text-[#53bdeb]';
  return 'text-n-slate-10';
});

const isMessageAnActivity = computed(() => {
  const type = props.message?.message_type ?? props.message?.messageType;
  return type === MESSAGE_TYPE.ACTIVITY || type === 'activity' || type === 2;
});

const isMessagePrivate = computed(() => {
  const { private: isPrivate } = props.message || {};
  return isPrivate;
});

const parsedLastMessage = computed(() => {
  const { content_attributes: contentAttributes } = props.message || {};
  const { email: { subject } = {} } = contentAttributes || {};
  return getPlainText(subject || props.message?.content);
});

const lastMessageFileType = computed(() => {
  const [{ file_type: fileType } = {}] = props.message?.attachments || [];
  return fileType;
});

const attachmentIcon = computed(() => {
  return attachmentIcons[lastMessageFileType.value];
});

const attachmentMessageContent = computed(() => {
  return `CHAT_LIST.ATTACHMENTS.${lastMessageFileType.value}.CONTENT`;
});

const isMessageSticker = computed(() => {
  const msg = props.message || {};
  const attrs = msg.content_attributes || msg.contentAttributes || {};
  const contentType = msg.content_type || msg.contentType;
  const attachments = msg.attachments || [];
  const firstAttachment = attachments[0] || {};
  const fileType = (firstAttachment.file_type || firstAttachment.fileType || '').toLowerCase();
  const ext = (firstAttachment.extension || '').toLowerCase();
  const mime = (firstAttachment.content_type || firstAttachment.contentType || '').toLowerCase();
  const url = (firstAttachment.data_url || firstAttachment.dataUrl || firstAttachment.file_url || firstAttachment.fileUrl || '').toLowerCase();
  const filename = (firstAttachment.title || firstAttachment.filename || '').toLowerCase();

  return (
    contentType === 'sticker' ||
    attrs.is_sticker === true ||
    attrs.isSticker === true ||
    attrs.is_sticker === 'true' ||
    attrs.isSticker === 'true' ||
    fileType === 'sticker' ||
    ext === 'webp' ||
    mime.includes('webp') ||
    url.includes('.webp') ||
    filename.includes('sticker') ||
    filename.includes('.webp')
  );
});

const isMessageAudio = computed(() => {
  const msg = props.message || {};
  const contentType = msg.content_type || msg.contentType;
  const attachments = msg.attachments || [];
  const firstAttachment = attachments[0] || {};
  const fileType = (firstAttachment.file_type || firstAttachment.fileType || lastMessageFileType.value || '').toLowerCase();

  return (
    contentType === 'audio' ||
    contentType === 'voice_call' ||
    fileType === 'audio' ||
    fileType === 'voice_call'
  );
});

const isMessageDeleted = computed(() => {
  const { content_attributes: attrs, content } = props.message || {};
  return (
    attrs?.is_deleted === true ||
    attrs?.deleted === true ||
    attrs?.isDeleted === true ||
    content === 'Mensagem apagada' ||
    content === 'Esta mensagem foi apagada'
  );
});
</script>

<template>
  <div
    class="min-w-0 text-sm"
    :class="
      multiLine
        ? 'flex items-start gap-1'
        : 'grid grid-cols-[auto_1fr] items-center gap-1'
    "
  >
    <template v-if="showMessageType && !multiLine">
      <Icon
        v-if="isMessagePrivate"
        icon="i-lucide-lock-keyhole"
        class="size-3.5"
      />
      <Icon
        v-else-if="messageByAgent"
        :icon="statusIcon"
        :class="statusColor"
        class="size-3.5 flex-shrink-0"
      />
      <Icon
        v-else-if="isMessageAnActivity"
        icon="i-lucide-info"
        class="size-3.5"
      />
    </template>

    <span
      class="min-w-0 text-body-main"
      :class="multiLine ? 'line-clamp-2' : 'truncate'"
    >
      <!-- Case for previous and conversation conversation card -->
      <template v-if="showMessageType && multiLine">
        <Icon
          v-if="isMessagePrivate"
          icon="i-lucide-lock-keyhole"
          class="inline-block align-middle size-3.5 ltr:mr-1 rtl:ml-1"
        />
        <Icon
          v-else-if="messageByAgent"
          :icon="statusIcon"
          :class="statusColor"
          class="inline-block align-middle size-3.5 ltr:mr-1 rtl:ml-1 flex-shrink-0"
        />
        <Icon
          v-else-if="isMessageAnActivity"
          icon="i-lucide-info"
          class="inline-block align-middle size-3.5 ltr:mr-1 rtl:ml-1"
        />
      </template>
      <span
        v-if="isMessageDeleted"
        class="inline-flex items-center gap-1 italic text-slate-400"
      >
        <Icon icon="i-lucide-ban" class="size-3.5 opacity-80" />
        <span>Mensagem apagada</span>
      </span>

      <span
        v-else-if="isMessageSticker"
        class="inline-grid grid-flow-col auto-cols-max items-center gap-1"
      >
        <span>✨ Figurinha</span>
      </span>

      <span
        v-else-if="isMessageAudio"
        class="inline-grid grid-flow-col auto-cols-max items-center gap-1"
      >
        <Icon icon="i-lucide-headphones" class="size-3.5" />
        <span>Mensagem de áudio</span>
      </span>

      <template v-else-if="message.content">
        {{ parsedLastMessage }}
      </template>

      <span
        v-else-if="message.attachments && message.attachments.length"
        class="inline-block align-middle truncate"
      >
        <Icon
          v-if="attachmentIcon && showMessageType"
          :icon="attachmentIcon"
          class="inline-block align-middle size-3.5 ltr:mr-1 rtl:ml-1"
        />
        <span class="inline-block align-middle">
          {{ $t(attachmentMessageContent) }}
        </span>
      </span>

      <template v-else>
        {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
      </template>
    </span>
  </div>
</template>
